import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../../config';
import Card from '../../common/Card';
import Button from '../../common/Button';
import FormInput from '../../common/FormInput';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import ConfirmDialog from '../../common/ConfirmDialog';
import ApiKeyCard from '../ApiKeyCard';
import './styles.css';

/**
 * 從 cookie 獲取指定名稱的值
 * @param {string} name - cookie 名稱
 * @returns {string|null} cookie 值或 null
 */
const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
    return null;
};

/**
 * API密鑰管理組件
 * 用於管理系統API密鑰
 * 
 * @returns {JSX.Element} API密鑰管理組件
 */
function ApiKeyManagement() {
    // 狀態管理
    const [apiKeys, setApiKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newKeyName, setNewKeyName] = useState('');
    const [newKeyGenerated, setNewKeyGenerated] = useState(null);
    const [showCopiedMessage, setShowCopiedMessage] = useState(false);
    const [formError, setFormError] = useState('');
    const [csrfToken, setCsrfToken] = useState(null);

    // 刪除確認對話框狀態
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteKeyId, setDeleteKeyId] = useState(null);

    // 獲取CSRF令牌
    const fetchCsrfToken = async () => {
        try {
            const response = await fetch('/api/csrf-token');
            if (!response.ok) {
                throw new Error('獲取CSRF令牌失敗');
            }
            const data = await response.json();
            // console.log('獲取到CSRF令牌:', data.csrfToken ? '已設置' : '未設置');
            setCsrfToken(data.csrfToken);
            return data.csrfToken;
        } catch (err) {
            console.error('獲取CSRF令牌時出錯:', err);
            return null;
        }
    };

    // 獲取所有API密鑰
    const fetchApiKeys = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem('token');
            let currentCsrfToken = csrfToken || getCookie('csrfToken');

            // 如果沒有CSRF令牌，嘗試獲取
            if (!currentCsrfToken) {
                currentCsrfToken = await fetchCsrfToken();
            }

            // console.log('獲取API密鑰，使用令牌:', token ? '已設置' : '未設置', '和CSRF令牌:', currentCsrfToken ? '已設置' : '未設置');

            const response = await fetch(`/api/keys`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-csrf-token': currentCsrfToken
                }
            });

            // console.log('API密鑰響應狀態:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('獲取API密鑰失敗:', errorText);
                throw new Error(`獲取API密鑰失敗: ${errorText}`);
            }

            const data = await response.json();
            // console.log('获取到的API密钥:', data);
            setApiKeys(data);
        } catch (err) {
            console.error('獲取API密鑰時出錯:', err);
            setError(err.message);

            // 如果API請求失敗，使用測試數據
            // console.log('使用測試數據作為後備');
            const testApiKeys = [
                {
                    id: 'test-key-1',
                    name: '測試密鑰 1 (活躍)',
                    prefix: 'ak_test_123456',
                    status: 'active',
                    createdAt: new Date().toISOString(),
                    lastUsed: new Date().toISOString()
                },
                {
                    id: 'test-key-2',
                    name: '測試密鑰 2 (已停用)',
                    prefix: 'ak_test_789012',
                    status: 'revoked',
                    createdAt: new Date().toISOString(),
                    lastUsed: null
                }
            ];
            setApiKeys(testApiKeys);
        } finally {
            setLoading(false);
        }
    };

    // 組件加載時獲取API密鑰
    useEffect(() => {
        // 首先獲取CSRF令牌
        fetchCsrfToken().then(() => {
            // 然後獲取API密鑰
            fetchApiKeys();
        });

        // 添加一些測試數據，確保UI正確顯示
        // 始終添加測試數據，不依賴環境變量
        const testApiKeys = [
            {
                id: 'test-key-1',
                name: '測試密鑰 1 (活躍)',
                prefix: 'ak_test_123456',
                status: 'active', // 確保狀態為 'active'
                createdAt: new Date().toISOString(),
                lastUsed: new Date().toISOString()
            },
            {
                id: 'test-key-2',
                name: '測試密鑰 2 (已停用)',
                prefix: 'ak_test_789012',
                status: 'revoked', // 確保狀態為 'revoked'
                createdAt: new Date().toISOString(),
                lastUsed: null
            }
        ];

        // 如果API沒有返回數據，使用測試數據
        setTimeout(() => {
            if (apiKeys.length === 0 && !loading) {
                console.log('使用測試數據');
                setApiKeys(testApiKeys);
            }
        }, 2000);
    }, []);

    // 創建新的API密鑰
    const handleCreateKey = async (e) => {
        e.preventDefault();

        // 驗證表單
        if (!newKeyName.trim()) {
            setFormError('請輸入API密鑰名稱');
            return;
        }

        setFormError('');
        setError(null);

        try {
            const token = localStorage.getItem('token');
            let currentCsrfToken = csrfToken || getCookie('csrfToken');

            // 如果沒有CSRF令牌，嘗試獲取
            if (!currentCsrfToken) {
                currentCsrfToken = await fetchCsrfToken();
            }

            // console.log('創建API密鑰，名稱:', newKeyName);
            // console.log('使用令牌:', token ? '已設置' : '未設置', '和CSRF令牌:', currentCsrfToken ? '已設置' : '未設置');

            const response = await fetch(`/api/keys`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'x-csrf-token': currentCsrfToken
                },
                body: JSON.stringify({ name: newKeyName })
            });

            // console.log('創建API密鑰響應狀態:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('創建API密鑰失敗:', errorText);
                throw new Error(`創建API密鑰失敗: ${errorText}`);
            }

            const data = await response.json();
            // console.log('創建的API密鑰:', data);

            // 確保新創建的密鑰狀態為活躍
            if (data && !data.status) {
                data.status = 'active';
            }

            setNewKeyGenerated(data);
            setNewKeyName('');

            // 刷新密鑰列表
            fetchApiKeys();
        } catch (err) {
            console.error('創建API密鑰時出錯:', err);
            setError(err.message);
        }
    };

    // 打開刪除對話框
    const openDeleteDialog = (keyId) => {
        setDeleteKeyId(keyId);
        setShowDeleteDialog(true);
    };

    // 關閉刪除對話框
    const closeDeleteDialog = () => {
        setShowDeleteDialog(false);
        setDeleteKeyId(null);
    };

    // 刪除API密鑰
    const handleDeleteKey = async () => {
        try {
            const token = localStorage.getItem('token');
            let currentCsrfToken = csrfToken || getCookie('csrfToken');

            // 如果沒有CSRF令牌，嘗試獲取
            if (!currentCsrfToken) {
                currentCsrfToken = await fetchCsrfToken();
            }

            // console.log(`嘗試刪除API密鑰 ${deleteKeyId}`);
            // console.log('使用令牌:', token ? '已設置' : '未設置', '和CSRF令牌:', currentCsrfToken ? '已設置' : '未設置');

            // 直接在前端更新狀態，提供即時反饋
            setApiKeys(apiKeys.filter(key => key.id !== deleteKeyId));

            // 關閉對話框
            closeDeleteDialog();

            // 使用正確的API端點刪除密鑰
            const response = await fetch(`/api/keys/${deleteKeyId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'x-csrf-token': currentCsrfToken
                }
            });

            // console.log('刪除API密鑰響應狀態:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('刪除API密鑰失敗:', errorText);

                // 如果API請求失敗，恢復前端狀態
                fetchApiKeys();

                throw new Error(`刪除API密鑰失敗: ${errorText}`);
            }

            // console.log(`成功刪除API密鑰 ${deleteKeyId}`);
        } catch (err) {
            console.error('刪除API密鑰時出錯:', err);
            setError(err.message);
        }
    };

    // 複製到剪貼板
    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(
            () => {
                setShowCopiedMessage(true);
                setTimeout(() => setShowCopiedMessage(false), 3000);
            },
            () => {
                setError('複製到剪貼板失敗');
            }
        );
    };

    // 關閉新密鑰提示
    const handleCloseNewKey = () => {
        setNewKeyGenerated(null);
        setShowCopiedMessage(false);
    };

    // 直接改變API密鑰狀態（不需要確認對話框）
    const handleChangeKeyStatus = async (keyId, newStatus) => {
        try {
            // console.log(`嘗試將API密鑰 ${keyId} 的狀態更改為 ${newStatus}`);

            // 直接在前端更新狀態，提供即時反饋
            setApiKeys(apiKeys.map(key =>
                key.id === keyId
                    ? { ...key, status: newStatus }
                    : key
            ));

            const token = localStorage.getItem('token');
            let currentCsrfToken = csrfToken || getCookie('csrfToken');

            // 如果沒有CSRF令牌，嘗試獲取
            if (!currentCsrfToken) {
                currentCsrfToken = await fetchCsrfToken();
            }

            // console.log('使用令牌:', token ? '已設置' : '未設置', '和CSRF令牌:', currentCsrfToken ? '已設置' : '未設置');

            let response;

            // 根據狀態使用不同的API端點
            if (newStatus === 'active') {
                // 使用啟用API密鑰的專用端點
                response = await fetch(`/api/keys/${keyId}/activate`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-csrf-token': currentCsrfToken,
                        'Content-Type': 'application/json'
                    }
                });
                // console.log(`使用POST請求到 /api/keys/${keyId}/activate 啟用密鑰`);
            } else if (newStatus === 'revoked') {
                // 使用停用API密鑰的專用端點
                response = await fetch(`/api/keys/${keyId}/revoke`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-csrf-token': currentCsrfToken,
                        'Content-Type': 'application/json'
                    }
                });
                // console.log(`使用POST請求到 /api/keys/${keyId}/revoke 停用密鑰`);
            } else {
                // 使用通用狀態更新端點作為後備
                response = await fetch(`/api/keys/${keyId}/status`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'x-csrf-token': currentCsrfToken,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ status: newStatus })
                });
                // console.log(`使用PUT請求到 /api/keys/${keyId}/status 更新密鑰狀態為 ${newStatus}`);
            }

            // console.log('API響應狀態:', response.status);

            if (!response.ok) {
                // 如果請求失敗，回滾前端狀態
                const errorText = await response.text();
                console.error(`更新API密鑰狀態失敗: ${errorText}`);

                // 回滾前端狀態
                setApiKeys(apiKeys.map(key =>
                    key.id === keyId
                        ? { ...key, status: newStatus === 'active' ? 'revoked' : 'active' }
                        : key
                ));

                throw new Error(`更改API密鑰狀態為${newStatus}失敗: ${errorText}`);
            }

            // console.log(`成功將API密鑰 ${keyId} 的狀態更改為 ${newStatus}`);

            // 刷新API密鑰列表
            fetchApiKeys();
        } catch (err) {
            console.error("更改API密鑰狀態時發生錯誤:", err);
            setError(`更改API密鑰狀態失敗: ${err.message}`);
        }
    };

    // 添加測試密鑰（僅用於開發環境）
    const addTestKey = () => {
        // 默認為活躍狀態
        const status = 'active';

        const newTestKey = {
            id: `test-key-${Date.now()}`,
            name: `測試密鑰 ${apiKeys.length + 1} (活躍)`,
            prefix: `ak_test_${Math.floor(Math.random() * 1000000)}`,
            status: status,
            createdAt: new Date().toISOString(),
            lastUsed: new Date().toISOString()
        };

        setApiKeys([...apiKeys, newTestKey]);

        // 顯示提示信息
        // console.log('添加了新的測試密鑰:', newTestKey);
    };

    // 加載中狀態
    if (loading && apiKeys.length === 0) {
        return <Loading text="正在加載API密鑰數據..." />;
    }

    return (
        <div className="api-key-management-container">
            {/* 頁面標題 */}
            <div className="api-key-management-header">
                <h2 className="api-key-management-title">API密鑰管理</h2>
                <Button
                    variant="secondary"
                    size="small"
                    onClick={addTestKey}
                >
                    添加測試密鑰
                </Button>
            </div>

            {/* 錯誤信息 */}
            {error && (
                <ErrorMessage
                    message={error}
                    onRetry={fetchApiKeys}
                />
            )}

            {/* 新密鑰生成提示 */}
            {newKeyGenerated && (
                <div className="new-key-alert">
                    <div className="new-key-header">
                        <h3>新API密鑰已生成</h3>
                        <Button
                            variant="secondary"
                            size="small"
                            onClick={handleCloseNewKey}
                            className="close-button"
                        >
                            ×
                        </Button>
                    </div>
                    <p className="warning-text">請立即複製您的API密鑰。出於安全原因，此密鑰只會顯示一次。</p>
                    <div className="key-display">
                        <code>{newKeyGenerated.key}</code>
                        <Button
                            variant="primary"
                            size="small"
                            onClick={() => copyToClipboard(newKeyGenerated.key)}
                        >
                            複製
                        </Button>
                    </div>
                    {showCopiedMessage && <div className="copied-message">已複製到剪貼板！</div>}
                </div>
            )}

            {/* 創建新密鑰表單 */}
            <Card title="創建新API密鑰" className="create-key-card">
                <form onSubmit={handleCreateKey} className="create-key-form">
                    <FormInput
                        id="keyName"
                        label="密鑰名稱"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        placeholder="輸入密鑰名稱（例如：生產環境、測試環境）"
                        required
                        error={formError}
                    />
                    <div className="form-actions">
                        <Button
                            type="submit"
                            variant="primary"
                        >
                            創建API密鑰
                        </Button>
                    </div>
                </form>
            </Card>

            {/* API密鑰列表 */}
            <Card title="API密鑰列表" className="api-keys-list-card">
                {loading && apiKeys.length === 0 ? (
                    <div className="api-keys-loading">
                        <Loading text="加載中..." size="small" />
                    </div>
                ) : apiKeys.length === 0 ? (
                    <div className="api-keys-empty">
                        <p>暫無API密鑰</p>
                    </div>
                ) : (
                    <>
                        <div className="api-keys-instructions">
                            <p>您可以使用以下按鈕管理API密鑰：</p>
                            <ul>
                                <li><strong>停用密鑰</strong> - 暫時停用API密鑰，可以隨時重新啟用（僅對活躍狀態的密鑰顯示）</li>
                                <li><strong>啟用密鑰</strong> - 重新啟用已停用的API密鑰（僅對已停用狀態的密鑰顯示）</li>
                                <li><strong>刪除密鑰</strong> - 從系統中永久刪除API密鑰，需要確認</li>
                            </ul>
                        </div>
                        <div className="api-keys-grid">
                            {apiKeys.map(apiKey => (
                                <ApiKeyCard
                                    key={apiKey.id}
                                    apiKey={apiKey}
                                    onDelete={openDeleteDialog}
                                    onChangeStatus={handleChangeKeyStatus}
                                />
                            ))}
                        </div>
                    </>
                )}
            </Card>

            {/* API使用說明 */}
            <Card title="API密鑰使用說明" className="api-usage-guide-card">
                <p>API密鑰用於授權對我們API的訪問。請妥善保管您的API密鑰，不要在公共場合分享。</p>
                <h4>如何使用API密鑰：</h4>
                <p>在API請求的Header中添加以下內容：</p>
                <pre>
                    <code>
                        Authorization: Bearer YOUR_API_KEY
                    </code>
                </pre>
                <p>更多API文檔請參考我們的<a href="/api-docs" target="_blank" rel="noopener noreferrer">API文檔</a>。</p>
            </Card>

            {/* 刪除確認對話框 */}
            <ConfirmDialog
                isOpen={showDeleteDialog}
                title="刪除API密鑰"
                message="確定要永久刪除此API密鑰嗎？此操作無法撤銷，刪除後此密鑰的所有記錄將被永久移除。如果此密鑰仍處於活躍狀態，刪除後將立即失效。"
                confirmText="刪除"
                cancelText="取消"
                onConfirm={handleDeleteKey}
                onCancel={closeDeleteDialog}
            />
        </div>
    );
}

export default ApiKeyManagement; 